/**
 * scopeSnapshotToSource — narrows a diagnostic-map snapshot to nodes whose
 * `sources` array contains a filename matching one or more target names.
 *
 * `fileName` may be a single string (backward-compatible) or an array of
 * strings (aggregate cards that span several runbooks). The result is the
 * UNION of every target's matched nodes — so an aggregate card's map shows
 * every runbook that contributed to it, with edges kept only when BOTH
 * endpoints survived.
 *
 * Returns `null` when no match is found (snapshot falsy, all names empty,
 * no nodes, or no node's sources match any name). Callers must handle
 * null — showing an unrelated document's graph mislabelled is worse than
 * showing nothing.
 *
 * The returned snapshot is always ISOLATED: session/traversal fields
 * (current_node_nid, current_node_type, visited_nodes, visited_node_details,
 * traversal_path, eliminated_causes, path_bridges) are removed so the map
 * renders as a pure structural view with no active/visited state.
 *
 * Matching mirrors the backend's `runbook_cache.is_causal_filename`:
 * 1. Exact match on normalised names.
 * 2. If tier 1 yields nothing, retry with the "Runbook - " prefix stripped
 *    from BOTH sides (see `runbook_cache._RUNBOOK_PREFIX_RE`). Qdrant stores
 *    "ZIA Slowness with ZCC.pdf" while the snapshot stores
 *    "Runbook - ZIA Slowness with ZCC.pdf"; this tier bridges that gap.
 *
 * Normalisation strips directory prefixes, known diagnostic extensions
 * (.mermaid.enc, .mermaid, .enc, .pdf), collapses separators, lowercases,
 * and trims. The comparison uses these normalised forms so that minor
 * differences between the backend payload and the KG node metadata do not
 * cause a false mismatch — including the ".pdf" vs no-".pdf" spelling of the
 * same document that reaches the frontend today.
 */

const normaliseName = (name) => {
  if (!name) return "";
  return String(name)
    .replace(/^.*[\\/]/, "")                              // strip directory prefix
    .toLowerCase()
    .trim()
    // Document extensions, mirroring runbook_cache._DOC_SUFFIXES, plus the
    // mermaid forms the graph side uses. Only ONE is stripped, and only at the
    // end, exactly as normalise_filename does it.
    .replace(/\.(mermaid\.enc|mermaid|enc|pdf|xlsx|xls|docx|doc|md|txt)$/i, "")
    // Collapse EVERY run of non-alphanumerics to a single space. This mirrors
    // runbook_cache.loose_filename, and it has to: ingestion stores
    // "Runbook - ZIA Slowness with ZCC.pdf" as "Runbook   ZIA Slowness with ZCC",
    // so a card filename and a graph node's source can differ purely in
    // punctuation and still be the same document. Anything narrower (collapsing
    // only [-_\s]) silently fails to match on names carrying dots, brackets or
    // ampersands, and a silent miss here is indistinguishable from "this
    // document has no runbook" — the user just gets the whole graph instead.
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

// stripRunbookPrefix — mirrors runbook_cache._strip_runbook_prefix.
// Applied AFTER normaliseName (so the input is already lowercased with
// punctuation collapsed to spaces). The original backend regex is
// ^runbook\s*[-_]?\s*  (case-insensitive). On a normalised string the
// separator between "runbook" and the rest is already a single space,
// so we match "runbook " at the start.
const stripRunbookPrefix = (normalised) => {
  if (!normalised) return "";
  return normalised.replace(/^runbook\s*/, "").trim();
};

/**
 * isolateSnapshot — returns a shallow copy of `snapshot` with the
 * session/traversal fields deleted entirely (not set to empty). This
 * ensures DiagnosticMapWindow.buildSnapshotGraph cannot re-derive a
 * visited set, active node, traversal path or Return-path bridge edge
 * from the snapshot: a standalone source map is a structural view of a
 * document, not a record of anyone's walk.
 */
export function isolateSnapshot(snapshot) {
  if (!snapshot) return snapshot;
  const copy = { ...snapshot };
  delete copy.current_node_nid;
  delete copy.current_node_type;
  delete copy.visited_nodes;
  delete copy.visited_node_details;
  delete copy.traversal_path;
  delete copy.eliminated_causes;
  // path_bridges paints real edges as red dashed "Return path". Those describe
  // a traversal, so they must go with it — otherwise an isolated map claims a
  // backtrack that never happened on this canvas.
  delete copy.path_bridges;
  return copy;
}

export default function scopeSnapshotToSource(snapshot, fileName) {
  // Normalise the argument: accept a single string or an array of strings.
  const raw = Array.isArray(fileName) ? fileName : [fileName];
  const targets = raw.map((n) => normaliseName(n)).filter(Boolean);

  if (!snapshot || !targets.length) return null;

  const subgraph = snapshot.diagnostic_subgraph;
  if (!subgraph?.nodes?.length) return null;

  // --- Tier 1: exact match on normalised names ---
  // Mirrors runbook_cache.is_causal_filename's first lookup.
  const targetSet = new Set(targets);
  const matchedNodes = subgraph.nodes.filter((node) =>
    (node.sources || []).some((src) => targetSet.has(normaliseName(src)))
  );

  if (matchedNodes.length > 0) {
    return buildResult(snapshot, subgraph, matchedNodes, (src) => targetSet.has(normaliseName(src)));
  }

  // --- Tier 2: prefix-stripped match ---
  // Mirrors runbook_cache.is_causal_filename / by_content_name: Qdrant stores
  // "ZIA Slowness with ZCC.pdf" while the snapshot stores "Runbook - ZIA
  // Slowness with ZCC.pdf". Strip the "Runbook" prefix from BOTH sides so
  // the two can still match.
  const strippedSet = new Set(
    targets.map((t) => stripRunbookPrefix(t)).filter(Boolean)
  );
  if (!strippedSet.size) return null;

  const matchesStripped = (src) => strippedSet.has(stripRunbookPrefix(normaliseName(src)));
  const prefixMatched = subgraph.nodes.filter((node) =>
    (node.sources || []).some(matchesStripped)
  );

  if (prefixMatched.length === 0) return null;

  return buildResult(snapshot, subgraph, prefixMatched, matchesStripped);
}

/**
 * buildResult — assembles the scoped, isolated snapshot from the matched
 * nodes. Extracted so both tier-1 and tier-2 paths share the same logic.
 *
 * `isMatch` identifies which of a node's raw source names caused the match, so
 * `selected_runbooks` can be narrowed to the documents actually on this canvas.
 * Left at the product graph's full list it would name every runbook the product
 * owns in the title pills above a single document's flowchart.
 */
function buildResult(snapshot, subgraph, matchedNodes, isMatch) {
  const keptNids = new Set(matchedNodes.map((n) => n.nid));

  // Keep edges whose both endpoints are in the kept set. Handle both
  // `from_nid`/`to_nid` (server format) and `from`/`to` (graph format).
  const edges = (subgraph.edges || []).filter((e) => {
    const from = e.from_nid ?? e.from;
    const to = e.to_nid ?? e.to;
    return keptNids.has(from) && keptNids.has(to);
  });

  const runbooks = [];
  for (const node of matchedNodes) {
    for (const src of node.sources || []) {
      if (isMatch(src) && !runbooks.includes(src)) runbooks.push(src);
    }
  }

  return isolateSnapshot({
    ...snapshot,
    selected_runbooks: runbooks,
    diagnostic_subgraph: { nodes: matchedNodes, edges },
  });
}
